import {createBrowserRouter, RouterProvider, Navigate, useLocation, useNavigate, useRouteError} from "react-router-dom";
import React, {useContext, createContext, useEffect} from 'react';
import {AuthContext, AuthProvider} from "./context/AuthContext";
import {FetchContext, FetchProvider} from "./context/FetchContext";
import Page from "./components/Page";
import BackgroundUploadsProvider from "./storage/BackgroundUploadsProvider";
import {HeartbeatProvider} from "./context/HeartbeatContext";
import { SessionEventsProvider } from "./context/SessionEventsContext";
import { trackSessionEvent } from "./analytics/sessionEvents";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import useMediaQuery from "@mui/material/useMediaQuery";
import { ThemeProvider } from "@mui/material/styles";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import SignalWifiOffOutlinedIcon from "@mui/icons-material/SignalWifiOffOutlined";
import {NewLogoIcon} from "./pages/NewHome/HomeIcons";
import SignInUserIntercomComponent from "./components/IntercomComponent";
import PrivacyPolicy from "./pages/Landing/PrivacyPolicy";
import TermsOfService from "./pages/Landing/Terms";
import SupportPage from "./pages/Landing/Support";
import ZoomGuide from "./pages/Landing/ZoomGuide";
import AppSumoRedeem from "./pages/Landing/AppSumoRedeem";
import NewStudioAndLobby from "./pages/NewStudio/NewStudioAndLobby";
import NewGuestStudioAndLobby from "./pages/NewGuestStudio/NewGuestStudioAndLobby";
import AgenticHome from "./pages/NewHome/AgenticHome";
import V2Editor from "./pages/V2Editor/V2Editor";
import MobileUploadPage from "./pages/MobileUpload/MobileUploadPage";
import V2InitialLandingPage from "./pages/V2InitialLandingPage/V2InitialLandingPage";
import V2SpeechCleanupLandingPage from "./pages/V2InitialLandingPage/V2SpeechCleanupLandingPage";
import V2BeigeSpeechEditorLandingPage from "./pages/V2InitialLandingPage/V2BeigeSpeechEditorLandingPage";
import ClipsLandingPage from "./pages/V2InitialLandingPage/ClipsLandingPage";
import WorkspaceInviteAccept from "./pages/WorkspaceInviteAccept";
import MyGoogleIcon from "./assets/MyGoogleIcon";
import { isLibrettoMacApp, isSupportedChromeEdgeOrBrave } from "./utils/browserSupport";
import getTheme from "./theme";

const isDesktopLocalMode = (() => {
  try {
    return typeof window !== 'undefined' && window.__LIBRETTO_DESKTOP_LOCAL__ === true;
  } catch {
    return false;
  }
})();

const CHUNK_RELOAD_GUARD_KEY = "__libretto_chunk_reload_guard_v2__";
const CHUNK_RELOAD_GUARD_TTL_MS = 5 * 60 * 1000;
const isChunkLoadErrorMessage = (message) =>
  /Loading chunk .* failed/i.test(message) ||
  /ChunkLoadError/i.test(message) ||
  /Failed to fetch dynamically imported module/i.test(message);

const tryRecoverFromChunkError = (rawError) => {
  const message = (() => {
    try {
      if (typeof rawError === "string") return rawError;
      if (rawError && typeof rawError === "object" && "message" in rawError) return String(rawError.message);
      return String(rawError ?? "");
    } catch {
      return "";
    }
  })();

  if (!isChunkLoadErrorMessage(message)) return;

  try {
    if (!navigator.onLine) return;
  } catch {}

  try {
    const now = Date.now();
    const signature = String(message ?? "").slice(0, 400);
    const raw = sessionStorage.getItem(CHUNK_RELOAD_GUARD_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    const lastSignature = String(parsed?.signature ?? "");
    const lastAt = Number(parsed?.at ?? 0);
    const isFreshRepeat =
      signature &&
      lastSignature === signature &&
      Number.isFinite(lastAt) &&
      now - lastAt < CHUNK_RELOAD_GUARD_TTL_MS;
    if (isFreshRepeat) return;

    sessionStorage.setItem(CHUNK_RELOAD_GUARD_KEY, JSON.stringify({ signature, at: now }));

    const url = new URL(window.location.href);
    url.searchParams.set("__reload", String(now));
    window.location.replace(url.toString());
  } catch {}
};

// NOTE: Avoid route-level lazy loading/code splitting.
// Missing chunk errors are extremely user-visible and can happen during deploy/cache mismatch windows.

const AuthenticatedRoute = ({children}) => {
  const auth = useContext(AuthContext);
  const location = useLocation();

  if (isDesktopLocalMode) {
    return children;
  }

  if (!auth.authReady) {
    return null;
  }

  if (auth.isUserAuthenticated()) {
    return (
      <HeartbeatProvider>
        {children}
      </HeartbeatProvider>
    );
  }
  if (location.pathname.startsWith("/mobile-upload/")) {
    const returnTo = location.pathname;
    const loginParams = new URLSearchParams({continue: returnTo});
    return <Navigate to={`/login?${loginParams.toString()}`} replace={true}/>;
  }

  return <Navigate to="/" replace={true}/>;
};

const RouteErrorPage = () => {
  const error = useRouteError();
  const navigate = useNavigate();

  const errorMessage = (() => {
    try {
      if (typeof error === 'string') return error;
      if (error && typeof error === 'object' && 'message' in error) return String(error.message);
      return String(error);
    } catch {
      return 'Unknown error';
    }
  })();

  const isOffline = (() => {
    try {
      return !navigator.onLine;
    } catch {
      return false;
    }
  })();

		  const isChunkLoadError =
		    /Loading chunk .* failed/i.test(errorMessage) ||
		    /ChunkLoadError/i.test(errorMessage) ||
		    /Failed to fetch dynamically imported module/i.test(errorMessage);

			  const title = isOffline ? 'You are offline' : 'That didn’t work';
			  const subtitle = isOffline
		    ? (isChunkLoadError
		        ? 'Reconnect to the internet to open this page.'
		        : 'Reconnect and try again.')
		    : 'Try reloading — this usually fixes it.';

		  // Auto-recover once per chunk-error signature (with TTL) to avoid reload loops.
		  useEffect(() => {
		    if (!isChunkLoadError) return;
		    if (isOffline) return;
		    try {
		      const now = Date.now();
		      const signature = String(errorMessage ?? "").slice(0, 400);
		      const raw = sessionStorage.getItem(CHUNK_RELOAD_GUARD_KEY);
		      const parsed = raw ? JSON.parse(raw) : null;
		      const lastSignature = String(parsed?.signature ?? "");
		      const lastAt = Number(parsed?.at ?? 0);
		      const isFreshRepeat =
		        signature &&
		        lastSignature === signature &&
		        Number.isFinite(lastAt) &&
		        now - lastAt < CHUNK_RELOAD_GUARD_TTL_MS;
		      if (isFreshRepeat) return;

		      sessionStorage.setItem(
		        CHUNK_RELOAD_GUARD_KEY,
		        JSON.stringify({ signature, at: now })
		      );

		      const url = new URL(window.location.href);
		      url.searchParams.set("__reload", String(now));
		      window.location.replace(url.toString());
		    } catch {}
		  }, [isChunkLoadError, isOffline, errorMessage]);

		  const buttonSx = {
		    borderRadius: '8px',
		    textTransform: 'none',
		    fontFamily: '"Inter", sans-serif',
	    fontSize: '13px',
	    fontWeight: 500,
	    padding: '8px 12px',
	    minHeight: '32px',
	  };

	  return (
	    <Box
	      sx={{
	        position: 'fixed',
	        inset: 0,
	        backgroundColor: 'var(--app-bg-canvas)',
	        display: 'flex',
	        alignItems: 'center',
	        justifyContent: 'center',
	        padding: '24px',
	        zIndex: 9999,
	      }}
	    >
	      <Box
	        sx={{
	          width: '100%',
	          maxWidth: '560px',
	          backgroundColor: 'var(--app-bg-surface)',
	          borderRadius: '16px',
	          border: '1px solid var(--app-border-subtle)',
	          boxShadow: '0 20px 60px rgba(15, 23, 42, 0.08)',
	          padding: '28px',
	        }}
		      >
		        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
		          <Box
		            sx={{
		              width: 28,
		              height: 28,
		              borderRadius: '10px',
		              border: '1px solid var(--app-border-subtle)',
		              backgroundColor: 'var(--app-bg-subtle)',
		              display: 'flex',
		              alignItems: 'center',
		              justifyContent: 'center',
		              flexShrink: 0,
		            }}
		          >
		            {isOffline ? (
		              <SignalWifiOffOutlinedIcon sx={{ fontSize: 18, color: 'var(--app-text-muted)' }} />
		            ) : (
		              <WarningAmberOutlinedIcon sx={{ fontSize: 18, color: 'var(--app-text-muted)' }} />
		            )}
		          </Box>
		          <Box sx={{ fontFamily: '"Inter", sans-serif', fontSize: '16px', fontWeight: 600, color: 'var(--app-text-primary)' }}>
		            {title}
		          </Box>
		        </Box>
	        <Box sx={{ fontFamily: '"Inter", sans-serif', fontSize: '13px', color: 'var(--app-text-muted)', mb: 2 }}>
	          {subtitle}
	        </Box>
	        <Box sx={{ display: 'flex', gap: 1 }}>
	          <Button
	            variant="contained"
	            disableElevation
	            onClick={() => window.location.reload()}
	            sx={{
	              ...buttonSx,
	              backgroundColor: '#000000',
	              '&:hover': { backgroundColor: '#111111' },
	            }}
	          >
	            Reload
	          </Button>
	          <Button
	            variant="outlined"
	            onClick={() => navigate('/dashboard')}
	            sx={{
	              ...buttonSx,
	              color: 'var(--app-text-primary)',
	              borderColor: 'var(--app-border-subtle)',
	              '&:hover': {
	                borderColor: 'var(--app-border-muted)',
	                backgroundColor: 'var(--app-bg-subtle)',
	              },
	            }}
		          >
		            Go to dashboard
		          </Button>
		        </Box>
		      </Box>
	    </Box>
	  );
	};

if (typeof process === 'undefined') {
  global.process = {
    env: { NODE_ENV: 'development' }
    // You can add more properties to this object if needed
  };
}

const getPendingWorkspaceInvitePath = () => {
  try {
    const workspaceId = String(window.localStorage.getItem("pendingWorkspaceInvite") || "").trim();
    return workspaceId ? `/workspace-invites/${encodeURIComponent(workspaceId)}` : null;
  } catch {
    return null;
  }
};

const UnAuthenticatedRoute = ({children, isCatchAll}) => {
  const auth = useContext(AuthContext);
  const location = useLocation();

  if (isDesktopLocalMode) {
    return <Navigate to="/dashboard" replace={true}/>;
  }

  if (!auth.authReady) {
    return isCatchAll ? null : children;
  }

  const pendingWorkspaceInvitePath = auth.authState?.userId
      ? getPendingWorkspaceInvitePath()
      : null;
  if (pendingWorkspaceInvitePath) {
    return <Navigate to={pendingWorkspaceInvitePath} replace={true}/>;
  }

  if (isCatchAll) {
    return auth.isUserAuthenticated()
        ? <Navigate to="/dashboard" replace={true}/>
        : <Navigate to="/" replace={true}/>;
  }


  if (!auth.isUserAuthenticated()) {
    return children;
  }
  const requestedPath = new URLSearchParams(location.search).get("continue");
  const continuePath = requestedPath?.startsWith("/mobile-upload/") ? requestedPath : null;
  return (
      <Navigate to={continuePath || "/dashboard"} replace={true}/>
  );
}

// Screen size context for components that need to know about small screen state
export const ScreenSizeContext = createContext({
  isSmallScreen: false,
});

// Hook to use screen size context
export const useScreenSize = () => useContext(ScreenSizeContext);

// Small screen blocker UI component
const SmallScreenBlocker = () => {
  const auth = useContext(AuthContext);

  const handleLogout = async () => {
    try {
      await auth?.logout?.();
    } catch {}
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'var(--app-bg-canvas)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        zIndex: 9999,
      }}
    >
      <Box
        sx={{
          maxWidth: '400px',
          textAlign: 'center',
        }}
      >
        {/* Libretto Logo */}
        <Box sx={{ mb: 3 }}>
            <NewLogoIcon/>
        </Box>
        <Box
          sx={{
            fontFamily: '"Inter", sans-serif',
            fontSize: '18px',
            fontWeight: 500,
            color: 'var(--app-text-primary)',
            lineHeight: 1.5,
            mb: 2,
          }}
        >
          Please switch to a larger screen to use Libretto
        </Box>
        <Box
          sx={{
            fontFamily: '"Inter", sans-serif',
            fontSize: '14px',
            fontWeight: 400,
            color: 'var(--app-text-muted)',
            lineHeight: 1.5,
          }}
        >
          Libretto's editor works best on screens 1000px or wider.
        </Box>
        {auth?.authReady && auth?.isUserAuthenticated?.() && (
          <Box sx={{ mt: 3 }}>
            <Button
              variant="outlined"
              onClick={handleLogout}
              sx={{
                textTransform: "none",
                fontSize: "13px",
                fontWeight: 500,
                borderRadius: "10px",
                px: 2.5,
                py: 0.75,
                borderColor: "var(--app-border-subtle)",
                color: "var(--app-text-primary)",
                "&:hover": {
                  borderColor: "var(--app-border-muted)",
                  backgroundColor: "var(--app-bg-subtle)",
                },
              }}
            >
              Log out
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const BrowserIconFrame = ({ children, sx = {} }) => {
  return (
    <Box
      component="span"
      sx={{
        width: 40,
        height: 40,
        borderRadius: '12px',
        border: '1px solid rgba(17, 24, 39, 0.08)',
        backgroundColor: '#FFFFFF',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.55)',
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

const GoogleBrowserIcon = () => (
  <BrowserIconFrame>
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
      <MyGoogleIcon />
    </Box>
  </BrowserIconFrame>
);

const EdgeBrowserIcon = () => (
  <BrowserIconFrame
    sx={{
      background: 'radial-gradient(circle at 30% 28%, #6EE7F9 0%, #2CB7E8 40%, #0B6DF6 72%, #0B4DCF 100%)',
      border: 'none',
    }}
  >
    <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true">
      <defs>
        <linearGradient id="edge-swoosh" x1="2" y1="4" x2="20" y2="22" gradientUnits="userSpaceOnUse">
          <stop stopColor="#B2F1FF" />
          <stop offset="1" stopColor="#22C55E" />
        </linearGradient>
      </defs>
      <path
        d="M19.9 14.2c0 3.7-3.1 6.6-7.4 6.6-3.9 0-6.8-2.2-7.7-5.5.8 1.1 2.3 1.8 4 1.8 2.7 0 4.7-1.7 4.7-4 0-2.4-2-4-4.9-4-2.7 0-5.1 1.5-6.1 3.8.5-4.9 4.4-8.3 9.4-8.3 4.7 0 8 3.3 8 7.6Z"
        fill="url(#edge-swoosh)"
      />
      <path
        d="M20.1 13.8c-.8-1.2-2.3-2-4.4-2-2.9 0-5.2 1.7-5.2 4.3 0 1.8 1.1 3.2 2.8 3.9-.2 0-.5.1-.8.1-4.4 0-7.3-2.8-7.3-6.8 0-.5 0-.9.1-1.3 1-2.2 3.1-3.5 5.7-3.5 5 0 9.1 2 9.1 5.3Z"
        fill="#0B6DF6"
        opacity="0.96"
      />
    </svg>
  </BrowserIconFrame>
);

const BraveBrowserIcon = () => (
  <BrowserIconFrame
    sx={{
      background: 'linear-gradient(145deg, #FF8A1A 0%, #FF5A1F 52%, #FF3D00 100%)',
      border: 'none',
    }}
  >
    <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M12 3.1 15.1 4l2.2-.3 1.7 2.1 2 .9-.8 8L12 21l-8.2-6.3-.8-8 2-.9 1.7-2.1 2.2.3L12 3.1Z"
        fill="#FFFFFF"
      />
      <path
        d="M12 6.2 9.6 6.8 8 8.9l.4 1.8-1.7 1.8 1.8 4.2 3.5 1.7 3.5-1.7 1.8-4.2-1.7-1.8.4-1.8-1.6-2.1L12 6.2Z"
        fill="#FF5A1F"
      />
      <path
        d="m9.4 10.2 1.2-.8h2.8l1.2.8-.3 1.7.8.8-.8 1.7-1.4.8h-2l-1.4-.8-.8-1.7.8-.8-.3-1.7Z"
        fill="#FFFFFF"
      />
      <path
        d="M10.5 12.1h.9l.6.6h1l.6-.6h.9l-.3 1-.8.5h-2.4l-.8-.5-.3-1Z"
        fill="#FF5A1F"
      />
    </svg>
  </BrowserIconFrame>
);

const browserDownloads = [
  {
    label: 'Google Chrome',
    sublabel: 'Official download',
    href: 'https://www.google.com/chrome/',
    Icon: GoogleBrowserIcon,
  },
  {
    label: 'Microsoft Edge',
    sublabel: 'Official download',
    href: 'https://www.microsoft.com/edge/download',
    Icon: EdgeBrowserIcon,
  },
  {
    label: 'Brave',
    sublabel: 'Official download',
    href: 'https://brave.com/download/',
    Icon: BraveBrowserIcon,
  },
];

const BrowserBlocker = ({ productName }) => {
  const navigate = useNavigate();

  useEffect(() => {
    const suffix = String(productName ?? '').trim().toLowerCase() || 'unknown';
    trackSessionEvent(`chrome_edge_only_gate_viewed_${suffix}`);
  }, [productName]);

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'var(--app-bg-canvas)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        zIndex: 9999,
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: '680px',
          backgroundColor: 'var(--app-bg-surface)',
          borderRadius: '16px',
          border: '1px solid var(--app-border-subtle)',
          boxShadow: '0 10px 30px rgba(17, 24, 39, 0.08)',
          padding: '28px',
          textAlign: 'center',
        }}
      >
        <Box sx={{ mb: 2 }}>
          <NewLogoIcon />
        </Box>

        <Box
          sx={{
            fontFamily: '"Inter", sans-serif',
            fontSize: '18px',
            fontWeight: 600,
            color: 'var(--app-text-primary)',
            lineHeight: 1.4,
            mb: 1,
          }}
        >
          Please switch to Chrome, Edge, or Brave.
        </Box>

        <Box
          sx={{
            fontFamily: '"Inter", sans-serif',
            fontSize: '14px',
            fontWeight: 400,
            color: 'var(--app-text-muted)',
            lineHeight: 1.6,
            mb: 3,
          }}
        >
          For this release, Libretto’s {productName} is only verified on desktop Chrome, Edge, and Brave.
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
            gap: '12px',
            mb: 2,
          }}
        >
          {browserDownloads.map((browser) => (
            <Button
              key={browser.label}
              component="a"
              href={browser.href}
              target="_blank"
              rel="noreferrer"
              sx={{
                justifyContent: 'flex-start',
                alignItems: 'stretch',
                textTransform: 'none',
                borderRadius: '14px',
                px: 1.5,
                py: 1.25,
                minHeight: 72,
                width: '100%',
                backgroundColor: 'var(--app-bg-subtle)',
                border: '1px solid var(--app-border-subtle)',
                color: 'var(--app-text-primary)',
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: 'var(--app-bg-surface)',
                  borderColor: 'var(--app-border-muted)',
                  boxShadow: '0 12px 24px rgba(17, 24, 39, 0.08)',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', textAlign: 'left' }}>
                <browser.Icon />
                <Box sx={{ minWidth: 0 }}>
                  <Box
                    sx={{
                      fontFamily: '"Inter", sans-serif',
                      fontSize: 14,
                      fontWeight: 600,
                      lineHeight: 1.2,
                      color: 'var(--app-text-primary)',
                    }}
                  >
                    {browser.label}
                  </Box>
                  <Box
                    sx={{
                      fontFamily: '"Inter", sans-serif',
                      fontSize: 12,
                      lineHeight: 1.3,
                      color: 'var(--app-text-muted)',
                      mt: 0.35,
                    }}
                  >
                    {browser.sublabel}
                  </Box>
                </Box>
              </Box>
            </Button>
          ))}
        </Box>

        <Button
          variant="text"
          onClick={() => navigate('/dashboard')}
          sx={{ textTransform: 'none', color: 'var(--app-text-muted)' }}
        >
          Back to home
        </Button>

        <Box sx={{ mt: 2, fontFamily: '"Inter", sans-serif', fontSize: 12, color: 'var(--app-text-subtle)', lineHeight: 1.5 }}>
          Safari, Firefox, and mobile browsers aren’t supported yet for Studio and Editor.
        </Box>
      </Box>
    </Box>
  );
};

// Screen size provider that wraps content
const ScreenSizeProvider = ({children}) => {
  const isSmallScreen = useMediaQuery('(max-width: 999px)');
  
  return (
    <ScreenSizeContext.Provider value={{ isSmallScreen }}>
      {children}
    </ScreenSizeContext.Provider>
  );
};

// HOC to wrap a route component with screen size blocker
const withScreenBlocker = (Component) => {
  return function WrappedComponent(props) {
    const isSmallScreen = useMediaQuery('(max-width: 999px)');

    // Keep the wrapped component mounted once it has rendered at least once.
    // This prevents disconnects (e.g. LiveKit) if the user temporarily resizes their window.
    const [hasMountedAtLeastOnce, setHasMountedAtLeastOnce] = React.useState(() => !isSmallScreen);
    React.useEffect(() => {
      if (!isSmallScreen) setHasMountedAtLeastOnce(true);
    }, [isSmallScreen]);

    return (
      <>
        {hasMountedAtLeastOnce && <Component {...props} />}
        {isSmallScreen && <SmallScreenBlocker />}
      </>
    );
  };
};

const withChromeEdgeBraveBlocker = (Component, { productName }) => {
  return function WrappedComponent(props) {
    // The macOS host deliberately runs the editor in WKWebView. Keep the public
    // Safari policy unchanged while allowing the trusted native runtime through.
    if (!isLibrettoMacApp() && !isSupportedChromeEdgeOrBrave()) {
      return <BrowserBlocker productName={productName} />;
    }
    return <Component {...props} />;
  };
};

const LightThemeShell = ({ children }) => {
  const lightTheme = React.useMemo(() => getTheme('light', () => {}), []);

  return (
    <ThemeProvider theme={lightTheme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: 'background.default',
          color: 'text.primary',
        }}
      >
        {children}
      </Box>
    </ThemeProvider>
  );
};

// Wrapped components that block on small screens
const BlockedV2Editor = withScreenBlocker(withChromeEdgeBraveBlocker(V2Editor, { productName: 'Editor' }));
const BlockedNewStudioAndLobby = withScreenBlocker(withChromeEdgeBraveBlocker(NewStudioAndLobby, { productName: 'Studio' }));
const BlockedNewGuestStudioAndLobby = withScreenBlocker(withChromeEdgeBraveBlocker(NewGuestStudioAndLobby, { productName: 'Studio' }));
const router = createBrowserRouter([
    {path: "/privacy", element: <PrivacyPolicy/>, errorElement: <RouteErrorPage />},
    {path: "/support", element: <LightThemeShell><SupportPage/></LightThemeShell>, errorElement: <RouteErrorPage />},
    {path: "/docs/zoom", element: <LightThemeShell><ZoomGuide/></LightThemeShell>, errorElement: <RouteErrorPage />},
    {path: "/redeem/appsumo", element: <LightThemeShell><AppSumoRedeem/></LightThemeShell>, errorElement: <RouteErrorPage />},
    {path: "/login", element: <UnAuthenticatedRoute><LightThemeShell><V2BeigeSpeechEditorLandingPage/></LightThemeShell></UnAuthenticatedRoute>, errorElement: <RouteErrorPage />},
    {path: "/logout", element: <UnAuthenticatedRoute><Navigate to="/"/></UnAuthenticatedRoute>, errorElement: <RouteErrorPage />},
    {path: "/signup", element: <UnAuthenticatedRoute><LightThemeShell><V2BeigeSpeechEditorLandingPage/></LightThemeShell></UnAuthenticatedRoute>, errorElement: <RouteErrorPage />},
    {path: "/", element: <UnAuthenticatedRoute><LightThemeShell><V2BeigeSpeechEditorLandingPage/></LightThemeShell></UnAuthenticatedRoute>, errorElement: <RouteErrorPage />},
    {path: "/terms", element: <TermsOfService/>, errorElement: <RouteErrorPage />},
    // {path: "/share/:token", element: <SharePage/>},
    {path: "/shorts", element: <UnAuthenticatedRoute><LightThemeShell><ClipsLandingPage/></LightThemeShell></UnAuthenticatedRoute>, errorElement: <RouteErrorPage />},
    {path: "/shorts/login", element: <UnAuthenticatedRoute><LightThemeShell><ClipsLandingPage/></LightThemeShell></UnAuthenticatedRoute>, errorElement: <RouteErrorPage />},
    {path: "/shorts/signup", element: <UnAuthenticatedRoute><LightThemeShell><ClipsLandingPage/></LightThemeShell></UnAuthenticatedRoute>, errorElement: <RouteErrorPage />},
    // {path: "/", element: <UnAuthenticatedRoute><LightThemeShell><V2SpeechEditorLandingPage/></LightThemeShell></UnAuthenticatedRoute>, errorElement: <RouteErrorPage />},
    {path: "/workspace-invites/:workspaceId", element: <WorkspaceInviteAccept/>, errorElement: <RouteErrorPage />},
    {path: "/mobile-upload/:projectId", element: <AuthenticatedRoute><LightThemeShell><MobileUploadPage/></LightThemeShell></AuthenticatedRoute>, errorElement: <RouteErrorPage />},
    {path: "/editor/:projectId?", element: <AuthenticatedRoute><BlockedV2Editor/></AuthenticatedRoute>, errorElement: <RouteErrorPage />},
    {path: "/guest/:roomName/:userFriendlyRoomName", element: <BlockedNewGuestStudioAndLobby/>, errorElement: <RouteErrorPage />},
    // {path: "/dashboard/:workspaceId", element: <AuthenticatedRoute><BlockedAgenticHome/></AuthenticatedRoute>},
    {path: "/dashboard", element: <AuthenticatedRoute><AgenticHome/></AuthenticatedRoute>, errorElement: <RouteErrorPage />},
    {path: "/studio/:projectId?/:title?/:assetId?", element: <AuthenticatedRoute><BlockedNewStudioAndLobby/></AuthenticatedRoute>, errorElement: <RouteErrorPage />},
    {
    path: "*", // Catch-all route
    element: <UnAuthenticatedRoute isCatchAll={true}><LightThemeShell><V2BeigeSpeechEditorLandingPage/></LightThemeShell></UnAuthenticatedRoute>, // Redirect based on login status
    errorElement: <RouteErrorPage />,
  },
]);

const kIsAuthenticated = 'isAuthenticatedOnLibretto';
const kLibrettoToken = 'librettoUserToken';
const kUserEmailVerified = 'librettoUserEmailVerified';

const App = () => {
  useEffect(() => {
    const onError = (event) => {
      tryRecoverFromChunkError(event?.error ?? event?.message);
    };
    const onUnhandledRejection = (event) => {
      tryRecoverFromChunkError(event?.reason);
    };

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onUnhandledRejection);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
    };
  }, []);

  return (
      <Page>
        <FetchProvider>
          <AuthProvider>
            <SessionEventsProvider>
                  <BackgroundUploadsProvider>
                <ScreenSizeProvider>
                  <RouterProvider router={router}/>
                </ScreenSizeProvider>
              </BackgroundUploadsProvider>
            </SessionEventsProvider>
          </AuthProvider>
        </FetchProvider>
      </Page>
  );
}

export default App;
